/**
 * Compliance calendar — the society's statutory clock (whitepaper §19).
 * Computed items read live data (AGM held?, MC meeting this month?, defaulter
 * flats, committee-absence cessation alerts); manual items are per-financial-
 * year check-offs (statutory audit, annual returns, E-2 intimation, fund
 * accruals, structural audit, federation dues) done by write-holders on
 * governance.compliance — the Hon. Secretary's grant. Every item carries its
 * citation; sorted worst-first (overdue → attention → due → ok).
 * Wired: GET /api/meetings/compliance, POST .../compliance/<key>/done|undo.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, EmptyState, Field, Input, Pill, Screen, SectionLabel,
  Sheet, Skeleton, TopBar,
} from '@/ui';

type Item = {
  key: string; kind: 'computed' | 'manual'; title: string; sub: string;
  citation: string; dueDate: string | null; daysLeft: number | null;
  status: 'ok' | 'due' | 'attention' | 'overdue'; detail: string | null;
};

const STATUS_PILL: Record<Item['status'], { s: 'approved' | 'pending' | 'rejected' | 'urgent'; l: string }> = {
  ok: { s: 'approved', l: 'OK' },
  due: { s: 'pending', l: 'PENDING' },
  attention: { s: 'urgent', l: 'DUE SOON' },
  overdue: { s: 'rejected', l: 'OVERDUE' },
};

function fmtDue(iso: string | null, daysLeft: number | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (daysLeft == null) return date;
  if (daysLeft < 0) return `${date} · ${-daysLeft} days ago`;
  return `${date} · ${daysLeft} days left`;
}

export default function ComplianceScreen() {
  const { colors } = useTheme();
  const [fy, setFy] = React.useState('');
  const [items, setItems] = React.useState<Item[]>([]);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');
  const [canWrite, setCanWrite] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [markFor, setMarkFor] = React.useState<Item | null>(null);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [body, me] = await Promise.all([
        api.get<{ fy: string; items: Item[] }>('/api/meetings/compliance'),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setFy(body.fy);
      setItems(body.items ?? []);
      setCanWrite((me.perms?.['governance.compliance'] ?? []).includes('write'));
      setState('ready');
    } catch (e) {
      setState((e as { status?: number })?.status === 403 ? 'forbidden' : 'error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const doMark = () => {
    if (!markFor || busy) return;
    setBusy(true);
    api.post<unknown>(`/api/meetings/compliance/${markFor.key}/done`, { note: note.trim() })
      .then(() => { setNotice(`${markFor.title} marked done for FY ${fy}.`); void load(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? 'Could not mark this done.'))
      .finally(() => { setBusy(false); setMarkFor(null); setNote(''); });
  };

  const doUndo = (item: Item) => {
    api.post<unknown>(`/api/meetings/compliance/${item.key}/undo`, {})
      .then(() => { setNotice(`${item.title} reopened.`); void load(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? 'Could not reopen.'));
  };

  return (
    <Screen scroll={false}>
      <TopBar title="Compliance" onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={92} />)}</View>
        ) : state === 'forbidden' ? (
          <EmptyState
            icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
            title="Committee only"
            body="The compliance calendar is for the committee and office bearers."
          />
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title="Couldn't load the calendar"
            body="Check your connection and try again."
            cta="Retry"
            onCta={() => { void load(); }}
          />
        ) : (
          <>
            <AppText variant="bodySm" tone="tertiary">
              The society&apos;s statutory clock for FY {fy}. Live items update
              themselves; check-offs are recorded with your name.
            </AppText>
            {notice ? (
              <AppText variant="bodySm" weight="500" style={{ marginTop: space.s3, color: colors.primary700 }}>
                {notice}
              </AppText>
            ) : null}
            <View style={{ gap: space.s3, marginTop: space.s4, marginBottom: space.s6 }}>
              {items.map((it) => {
                const pill = STATUS_PILL[it.status];
                return (
                  <Card key={it.key}>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <AppText variant="bodyMd" weight="600">{it.title}</AppText>
                        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{it.sub}</AppText>
                      </View>
                      <Pill status={pill.s} label={pill.l} />
                    </View>
                    {it.dueDate ? (
                      <AppText variant="bodySm" weight="500" style={{ marginTop: space.s2 }}>
                        Due {fmtDue(it.dueDate, it.daysLeft)}
                      </AppText>
                    ) : null}
                    {it.detail ? (
                      <AppText variant="bodySm" tone="secondary" style={{ marginTop: it.dueDate ? 2 : space.s2 }}>
                        {it.detail}
                      </AppText>
                    ) : null}
                    <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
                      <Icon name="lock" size={12} color={colors.accent700} />
                      <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{it.citation}</AppText>
                    </View>
                    {it.kind === 'manual' && canWrite ? (
                      <View style={{ flexDirection: 'row', marginTop: space.s3 }}>
                        {it.status === 'ok' ? (
                          <SmallButton label="Reopen" onPress={() => doUndo(it)} />
                        ) : (
                          <SmallButton label="Mark done" icon="check"
                            onPress={() => { setMarkFor(it); setNote(''); setNotice(null); }} />
                        )}
                      </View>
                    ) : null}
                  </Card>
                );
              })}
            </View>
          </>
        )}
      </Screen>

      <Sheet visible={markFor !== null} onClose={() => setMarkFor(null)}>
        {markFor ? (
          <>
            <SectionLabel>Mark done · FY {fy}</SectionLabel>
            <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>{markFor.title}</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              Recorded with your name on the society&apos;s audit trail.
            </AppText>
            <View style={{ marginTop: space.s4 }}>
              <Field label="Note" help="Optional — auditor name, receipt number, resolution date…">
                <Input value={note} onChangeText={setNote} placeholder="e.g. completed 12 Jul, report filed" maxLength={250} />
              </Field>
            </View>
            <Button title={busy ? 'Recording…' : 'Mark done'} disabled={busy}
              style={{ marginTop: space.s4 }} onPress={doMark} />
          </>
        ) : null}
      </Sheet>
    </Screen>
  );
}
